// src/pages/marketplace/developer/DeveloperProductCreatePage.tsx
// Create new product form for the developer portal.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Textarea,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Badge,
  ErrorAlert,
} from '@react/components';

const CAPABILITIES = [
  'RESOURCE_PROVIDER',
  'RESOURCE_CONSUMER',
  'USER_PROFILES',
  'TEAMS',
  'BILLING',
  'SUBSCRIPTIONS',
  'MARKETPLACE',
  'WEBHOOKS',
  'API',
  'EMBEDDABLE',
] as const;

interface FormData {
  slug: string;
  name: string;
  description: string;
  tagline: string;
  legal_entity: string;
  legal_entity_id: string;
  icon_url: string;
  logo_url: string;
  color_primary: string;
  color_secondary: string;
  web_url: string;
  api_base_url: string;
  docs_url: string;
  support_url: string;
  redirect_uris: string;
  allowed_scopes: string;
  allowed_grant_types: string;
  oauth2_client_id: string;
  capabilities: string[];
}

const initialFormData: FormData = {
  slug: '',
  name: '',
  description: '',
  tagline: '',
  legal_entity: '',
  legal_entity_id: '',
  icon_url: '',
  logo_url: '',
  color_primary: '#3B82F6',
  color_secondary: '#1E40AF',
  web_url: '',
  api_base_url: '',
  docs_url: '',
  support_url: '',
  redirect_uris: '',
  allowed_scopes: '',
  allowed_grant_types: '',
  oauth2_client_id: '',
  capabilities: [],
};

export const DeveloperProductCreatePage = observer(() => {
  const navigate = useNavigate();
  const store = useProduct();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({ ...initialFormData });

  const handleChange = (field: keyof FormData, value: string | boolean | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCapabilityToggle = (cap: string) => {
    setFormData((prev) => ({
      ...prev,
      capabilities: prev.capabilities.includes(cap)
        ? prev.capabilities.filter((c) => c !== cap)
        : [...prev.capabilities, cap],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload: any = {
        ...formData,
        redirect_uris: formData.redirect_uris
          ? formData.redirect_uris.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        allowed_scopes: formData.allowed_scopes
          ? formData.allowed_scopes.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        allowed_grant_types: formData.allowed_grant_types
          ? formData.allowed_grant_types.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
      };
      const result = await store.CreateProduct(payload);
      const newId = (result as any)?.entity?.id || (result as any)?.id;
      if (newId) {
        navigate(`/developer/products/${newId}/edit`);
      } else {
        navigate('/developer');
      }
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb. The tab already names this page; the heading stays for screen readers. */}
      <div>
        <nav className="text-sm">
          <Link to="/developer" className="text-muted-foreground hover:text-foreground">
            Developer
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">Create Product</span>
        </nav>
        <h1 className="sr-only">Create Product</h1>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Info</CardTitle>
            <CardDescription>Core product information visible across the platform.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Input
                label="Slug"
                type="text"
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                placeholder="my-product"
                required
              />
              {formData.slug && (
                <p className="text-xs text-muted-foreground mt-1">
                  URL: /marketplace/<span className="font-mono">{formData.slug}</span>
                </p>
              )}
            </div>
            <Input
              label="Name"
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="My Product"
              required
            />
            <Textarea
              label="Description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe what your product does..."
              rows={4}
              required
            />
            <Input
              label="Tagline"
              type="text"
              value={formData.tagline}
              onChange={(e) => handleChange('tagline', e.target.value)}
              placeholder="A short tagline for your product"
            />
          </CardContent>
        </Card>

        {/* Legal */}
        <Card>
          <CardHeader>
            <CardTitle>Legal</CardTitle>
            <CardDescription>Legal entity information for the product owner.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Legal Entity"
              type="text"
              value={formData.legal_entity}
              onChange={(e) => handleChange('legal_entity', e.target.value)}
              placeholder="Company Name LLC"
            />
            <Input
              label="Legal Entity ID"
              type="text"
              value={formData.legal_entity_id}
              onChange={(e) => handleChange('legal_entity_id', e.target.value)}
              placeholder="Tax ID or registration number"
            />
          </CardContent>
        </Card>

        {/* Branding */}
        <Card>
          <CardHeader>
            <CardTitle>Branding</CardTitle>
            <CardDescription>Visual identity for your product.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Icon URL"
              type="url"
              value={formData.icon_url}
              onChange={(e) => handleChange('icon_url', e.target.value)}
              placeholder="https://example.com/icon.png"
            />
            <Input
              label="Logo URL"
              type="url"
              value={formData.logo_url}
              onChange={(e) => handleChange('logo_url', e.target.value)}
              placeholder="https://example.com/logo.png"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Input
                  label="Primary Color"
                  type="text"
                  value={formData.color_primary}
                  onChange={(e) => handleChange('color_primary', e.target.value)}
                  placeholder="#3B82F6"
                />
                <div className="flex items-center gap-2 mt-2">
                  <div
                    className="w-8 h-8 rounded border border-border"
                    style={{ backgroundColor: formData.color_primary || '#transparent' }}
                  />
                  <span className="text-xs text-muted-foreground font-mono">
                    {formData.color_primary || 'No color set'}
                  </span>
                </div>
              </div>
              <div>
                <Input
                  label="Secondary Color"
                  type="text"
                  value={formData.color_secondary}
                  onChange={(e) => handleChange('color_secondary', e.target.value)}
                  placeholder="#1E40AF"
                />
                <div className="flex items-center gap-2 mt-2">
                  <div
                    className="w-8 h-8 rounded border border-border"
                    style={{ backgroundColor: formData.color_secondary || '#transparent' }}
                  />
                  <span className="text-xs text-muted-foreground font-mono">
                    {formData.color_secondary || 'No color set'}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* URLs */}
        <Card>
          <CardHeader>
            <CardTitle>URLs</CardTitle>
            <CardDescription>Endpoints and documentation links.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Website URL"
              type="url"
              value={formData.web_url}
              onChange={(e) => handleChange('web_url', e.target.value)}
              placeholder="https://myproduct.com"
            />
            <Input
              label="API Base URL"
              type="url"
              value={formData.api_base_url}
              onChange={(e) => handleChange('api_base_url', e.target.value)}
              placeholder="https://api.myproduct.com"
            />
            <Input
              label="Documentation URL"
              type="url"
              value={formData.docs_url}
              onChange={(e) => handleChange('docs_url', e.target.value)}
              placeholder="https://docs.myproduct.com"
            />
            <Input
              label="Support URL"
              type="url"
              value={formData.support_url}
              onChange={(e) => handleChange('support_url', e.target.value)}
              placeholder="https://support.myproduct.com"
            />
          </CardContent>
        </Card>

        {/* OAuth2 */}
        <Card>
          <CardHeader>
            <CardTitle>OAuth2 Configuration</CardTitle>
            <CardDescription>Authentication and authorization settings.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="OAuth2 Client ID"
              type="text"
              value={formData.oauth2_client_id}
              onChange={(e) => handleChange('oauth2_client_id', e.target.value)}
              placeholder="client-id"
            />
            <div>
              <Input
                label="Redirect URIs"
                type="text"
                value={formData.redirect_uris}
                onChange={(e) => handleChange('redirect_uris', e.target.value)}
                placeholder="https://app.example.com/callback, https://localhost:3000/callback"
              />
              <p className="text-xs text-muted-foreground mt-1">Comma-separated list of allowed redirect URIs.</p>
              {formData.redirect_uris && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.redirect_uris.split(',').map((uri) => uri.trim()).filter(Boolean).map((uri, i) => (
                    <Badge key={i} variant="secondary">{uri}</Badge>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Input
                label="Allowed Scopes"
                type="text"
                value={formData.allowed_scopes}
                onChange={(e) => handleChange('allowed_scopes', e.target.value)}
                placeholder="read, write, admin"
              />
              <p className="text-xs text-muted-foreground mt-1">Comma-separated list of OAuth2 scopes.</p>
              {formData.allowed_scopes && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.allowed_scopes.split(',').map((s) => s.trim()).filter(Boolean).map((scope, i) => (
                    <Badge key={i} variant="secondary">{scope}</Badge>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Input
                label="Allowed Grant Types"
                type="text"
                value={formData.allowed_grant_types}
                onChange={(e) => handleChange('allowed_grant_types', e.target.value)}
                placeholder="authorization_code, client_credentials"
              />
              <p className="text-xs text-muted-foreground mt-1">Comma-separated list of grant types.</p>
              {formData.allowed_grant_types && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.allowed_grant_types.split(',').map((s) => s.trim()).filter(Boolean).map((gt, i) => (
                    <Badge key={i} variant="secondary">{gt}</Badge>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Capabilities */}
        <Card>
          <CardHeader>
            <CardTitle>Capabilities</CardTitle>
            <CardDescription>Select the capabilities your product provides.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {CAPABILITIES.map((cap) => (
                <label key={cap} className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.capabilities.includes(cap)}
                    onChange={() => handleCapabilityToggle(cap)}
                    className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                  />
                  <span className="text-sm font-medium text-foreground">
                    {cap.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                  </span>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="flex justify-end gap-3">
          <Link to="/developer">
            <Button type="button" variant="secondary">{t('common.cancel')}</Button>
          </Link>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Product'}
          </Button>
        </div>
      </form>
    </div>
  );
});

export default DeveloperProductCreatePage;
